import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

type FilterValue = string | string[] | undefined;
export type FiltersShape = Record<string, FilterValue>;
export type FilterQuery = Record<string, string | string[]>;

function buildQuery(filters: FiltersShape): FilterQuery {
    const result: FilterQuery = {};
    for (const [key, val] of Object.entries(filters)) {
        if (val === undefined || val === "") continue;
        if (Array.isArray(val)) {
            if (val.length > 0) result[key] = val;
        } else {
            result[key] = val;
        }
    }
    return result;
}

export default function useSearchFilters<T extends FiltersShape>(defaultState: T) {
    const [searchParams, setSearchParams] = useSearchParams();

    const parseFromUrl = (): T => {
        const result = { ...defaultState };
        for (const key of Object.keys(defaultState)) {
            const vals = searchParams.getAll(key);
            if (vals.length === 0) continue;
            if (Array.isArray(defaultState[key])) {
                (result as any)[key] = vals;
            } else {
                (result as any)[key] = vals[0];
            }
        }
        return result;
    };

    const [committed, setCommitted] = useState<T>(parseFromUrl);
    const [draft, setDraft] = useState<T>(parseFromUrl);
    const [filterOpen, setFilterOpen] = useState(false);

    const hasActiveFilters = useMemo(() => {
        return Object.values(committed).some((v) => {
            if (v === undefined || v === "") return false;
            if (Array.isArray(v)) return v.length > 0;
            return true;
        });
    }, [committed]);

    const syncToUrl = (filters: T) => {
        const params = new URLSearchParams();
        for (const [key, val] of Object.entries(filters)) {
            if (Array.isArray(val)) val.forEach((v) => params.append(key, v));
            else if (val !== undefined && val !== "") params.set(key, val);
        }
        setSearchParams(params, { replace: true });
    };

    const openFilter = () => {
        setDraft({ ...committed });
        setFilterOpen(true);
    };

    const saveFilter = (callback?: (query: FilterQuery) => void) => {
        const newQuery = buildQuery(draft);
        setCommitted({ ...draft });
        syncToUrl(draft);
        setFilterOpen(false);
        callback?.(newQuery);
    };

    const clearFilter = (callback?: () => void) => {
        const empty = Object.fromEntries(
            Object.keys(defaultState).map((k) => [k, Array.isArray(defaultState[k]) ? ([] as string[]) : undefined])
        ) as T;
        setCommitted(empty);
        setDraft(empty);
        setSearchParams({}, { replace: true });
        setFilterOpen(false);
        callback?.();
    };

    const cancelFilter = () => {
        setDraft({ ...committed });
        setFilterOpen(false);
    };

    const setDraftField = <K extends keyof T>(key: K, value: T[K]) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    };

    return {
        committed,
        committedQuery: buildQuery(committed),
        draft,
        setDraftField,
        filterOpen,
        openFilter,
        saveFilter,
        clearFilter,
        cancelFilter,
        hasActiveFilters,
    };
}
